// Which brand mark the authenticated rail carries.
//
// By default it is Spear's. A white-label partner gets their own instead: every
// supplier the account can see is branded to the partner, not to Spear. This is
// the only place that decision is made.
//
// Keyed on the account's `fname` AND `username`, because either can hold the
// partner name depending on how the admin filled the Add User form (`fname` is
// the supplier code for supplier-type accounts, but not reliably), and the
// failure mode of a miss is silent — the rail just shows Spear and nobody knows
// the branding was meant to be different. Matching both is cheap insurance.
//
// `fit` tells the rail how to draw the asset:
//   "crop"    — the source is a square with dead padding around the glyph, so it
//               is oversized and clipped (see the note in Sidebar.jsx).
//   "contain" — the source is already trimmed; draw it whole.
// `plate: true` puts the mark on a light panel, for assets with an opaque
// light background that would otherwise read as a torn hole in the dark rail.

export const SPEAR_MARK = {
  src: "/spearbi_ai4.png",
  label: "Spear Management",
  fit: "crop",
  plate: false,
};

const PARTNER_MARKS = {
  GATEWAY: {
    src: "/GATEWAY.jpg",
    label: "SG Gateway",
    // A trimmed 1600x674 wordmark on an opaque white ground — nothing like the
    // padded square Spear mark, so it must not inherit that crop.
    fit: "contain",
    plate: true,
  },
};

const normalise = (value) => String(value || "").trim().toUpperCase();

// Matched as a SUBSTRING, not an exact key. The admin form decides what goes in
// `fname` and `username`, and "SG GATEWAY", "Gateway (SG)" and "GATEWAY" are all
// plausible — an exact match that misses shows Spear and reports nothing, so the
// loose test is the safer default for a mark that is meant to be obvious. It was
// checked against every existing account: none contains a partner key, so no
// account can pick up partner branding by accident. Keep the keys distinctive
// enough that that stays true.
const matchPartner = (...identities) => {
  const haystack = identities.map(normalise).join(" ");
  if (!haystack.trim()) return null;
  const key = Object.keys(PARTNER_MARKS).find((partner) => haystack.includes(partner));
  return key ? PARTNER_MARKS[key] : null;
};

// Ordered logo sources for one stored code: static same-host files first
// (mirrored into the frontend public folder by supplierLogoService), then the
// API resolver. Same contract as reminder 10 — never put the API first.
const logoCandidates = (storedCode) => {
  const code = encodeURIComponent(storedCode);
  const candidates = [`/${code}.jpg`, `/${code}.png`];
  const apiBase = process.env.REACT_APP_API_URL;
  if (apiBase) candidates.push(`${apiBase}/admin/supplier-logo/${code}`);
  return candidates;
};

/**
 * @param {object} [identity] override for tests — {fname, username, user_type,
 *   userid}. Omitted in the app, which reads the values the sign-in response
 *   wrote to localStorage.
 * @returns {{src: string, label: string, fit: string, plate: boolean,
 *   candidates?: string[], fallback?: object}}
 *
 * Branding tiers (most specific wins): a CAM's own uploaded logo (stored in the
 * supplier-logo pipeline as CAM_<userid> — TASK-user-creation-rework phase 1),
 * then organisation branding for members of a non-SPEARMGMT org (ORG_<code>,
 * admin-v2 phase 4). Both tiers are flattened into ONE `candidates` list — the
 * rail walks it source-by-source, so a CAM without a personal logo falls
 * through to their org's mark automatically. When every candidate 404s the
 * rail falls back to `fallback` — the legacy hand-coded partner mark if the
 * account matches one, else Spear's. The org code/name are written to
 * localStorage by SupplierRetailerContext on every access fetch.
 */
export const resolveBrandMark = (identity) => {
  let fname = identity && identity.fname;
  let username = identity && identity.username;
  let userType = identity && identity.user_type;
  let userid = identity && identity.userid;
  let orgCode = null;
  let orgName = null;

  if (!identity) {
    try {
      fname = localStorage.getItem("fname");
      username = localStorage.getItem("username");
      userType = localStorage.getItem("user_type");
      userid = localStorage.getItem("userid");
      orgCode = localStorage.getItem("user_org_code");
      orgName = localStorage.getItem("user_org_name");
    } catch (e) {
      return SPEAR_MARK;
    }
  }

  const legacy = matchPartner(fname, username) || SPEAR_MARK;

  const candidates = [];
  let label = null;
  if (userType === "CAM" && userid) {
    candidates.push(...logoCandidates(`CAM_${userid}`));
    label = fname || "CAM";
  }
  if (orgCode && orgCode !== "SPEARMGMT") {
    candidates.push(...logoCandidates(`ORG_${orgCode}`));
    if (!label) label = orgName || orgCode;
  }

  if (candidates.length) {
    return {
      src: candidates[0],
      candidates,
      fallback: legacy,
      label,
      fit: "contain",
      plate: true,
    };
  }

  return legacy;
};

export default resolveBrandMark;
